import {
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import { ExpiringDocumentDto } from "../../../api/documentsApi";
import {
  Action,
  Column,
  DataTable,
} from "../../../components/common/DataTable/DataTable";
import { SearchableColumn } from "../../../components/common/SearchInputs";

interface ExpiringDocumentsTableProps {
  documents: ExpiringDocumentDto[];
  isLoading: boolean;
  totalCount?: number;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  isLoadingRows?: boolean;
  onLoadMore?: () => void;
  onSearchChange?: (filters: Record<string, any>) => void;
  initialTableFilters?: Record<string, any>;
}

const ENTITY_TYPE_OPTIONS: {
  value: string | null;
  tabValue: string;
  label: string;
}[] = [
  { value: null, tabValue: "all", label: "All" },
  { value: "Vehicle", tabValue: "Vehicle", label: "Vehicles" },
  { value: "Trailer", tabValue: "Trailer", label: "Trailers" },
  { value: "employee", tabValue: "employee", label: "Drivers" },
];

const CATEGORY_OPTIONS = [
  { value: "Insurance", label: "Insurance" },
  { value: "Registration", label: "Registration" },
  { value: "Inspection", label: "DOT Inspection" },
  { value: "Maintenance", label: "Maintenance Records" },
  { value: "License", label: "License/Permits" },
  { value: "Manual", label: "Owner's Manual" },
  { value: "Warranty", label: "Warranty Information" },
  { value: "Other", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "expired", label: "Expired" },
  { value: "critical", label: "7 days or less" },
  { value: "warning", label: "8-30 days" },
  { value: "valid", label: "31+ days" },
];

const getStatusChip = (daysUntil: number, isExpired: boolean) => {
  if (isExpired) {
    return (
      <Chip
        label="Expired"
        size="small"
        color="error"
        variant="filled"
        sx={{ fontSize: "0.75rem" }}
      />
    );
  }
  if (daysUntil <= 7) {
    return (
      <Chip
        label={`${daysUntil}d left`}
        size="small"
        color="error"
        variant="outlined"
        sx={{ fontSize: "0.75rem" }}
      />
    );
  }
  if (daysUntil <= 30) {
    return (
      <Chip
        label={`${daysUntil}d left`}
        size="small"
        color="warning"
        variant="outlined"
        sx={{ fontSize: "0.75rem" }}
      />
    );
  }
  return (
    <Chip
      label={`${daysUntil}d left`}
      size="small"
      color="success"
      variant="outlined"
      sx={{ fontSize: "0.75rem" }}
    />
  );
};

const getEntityTypeIcon = (entityType: string) => {
  switch (entityType) {
    case "Vehicle":
      return "🚛";
    case "Trailer":
      return "🚚";
    default:
      return "👤";
  }
};

const COLLAPSED_DOC_LIMIT = 3;

const DOCUMENT_COLUMNS: Column[] = [
  {
    field: "search",
    headerName: "Document",
    flex: 1.5,
    renderCell: (_value, row: ExpiringDocumentDto) => (
      <Typography
        sx={{ fontSize: "0.8125rem" }}
        noWrap
        title={row.originalFileName}
      >
        {row.originalFileName}
      </Typography>
    ),
  },
  {
    field: "documentCategory",
    headerName: "Category",
    flex: 1,
    renderCell: (value) => (
      <Typography sx={{ fontSize: "0.8125rem" }}>{value || "-"}</Typography>
    ),
  },
  {
    field: "expirationDate",
    headerName: "Expiration Date",
    flex: 1,
    renderCell: (value) => (
      <Typography sx={{ fontSize: "0.8125rem" }}>
        {value
          ? new Date(value).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "-"}
      </Typography>
    ),
  },
  {
    field: "daysUntilExpiration",
    headerName: "Status",
    flex: 0.8,
    renderCell: (_value, row: ExpiringDocumentDto) =>
      getStatusChip(row.daysUntilExpiration, row.isExpired),
  },
  {
    field: "isPermit",
    headerName: "Permit",
    flex: 0.5,
    renderCell: (_value, row: ExpiringDocumentDto) =>
      row.isPermit ? (
        <Chip
          label="Yes"
          size="small"
          color="info"
          variant="outlined"
          sx={{ fontSize: "0.75rem" }}
        />
      ) : (
        <Typography sx={{ fontSize: "0.8125rem" }}>-</Typography>
      ),
  },
];

const SEARCHABLE_COLUMNS: SearchableColumn[] = [
  {
    field: "search",
    headerName: "Document",
    searchable: true,
    searchType: "string",
  },
  {
    field: "documentCategory",
    headerName: "Category",
    searchable: true,
    searchType: "select",
    selectOptions: CATEGORY_OPTIONS,
  },
  {
    field: "daysUntilExpiration",
    headerName: "Status",
    searchable: true,
    searchType: "select",
    selectOptions: STATUS_OPTIONS,
  },
];

const DOCUMENT_ACTIONS: Action<ExpiringDocumentDto>[] = [
  {
    icon: <OpenIcon fontSize="small" />,
    label: "View Document",
    onClick: (document) => window.open(document.url, "_blank"),
    color: "primary",
  },
];

const ExpiringDocumentsTable: React.FC<ExpiringDocumentsTableProps> = ({
  documents,
  isLoading,
  totalCount,
  hasNextPage,
  isLoadingMore,
  isLoadingRows,
  onLoadMore,
  onSearchChange,
  initialTableFilters,
}) => {
  const [entityTypeFilter, setEntityTypeFilter] = useState<string | null>(null);
  const [tableFilters, setTableFilters] = useState<Record<string, any>>(
    () => initialTableFilters ?? {},
  );

  const buildFilters = useCallback(
    (nextTableFilters: Record<string, any>, nextEntityType: string | null) => ({
      ...nextTableFilters,
      ...(nextEntityType ? { entityType: nextEntityType } : {}),
    }),
    []
  );

  const handleTableSearchChange = useCallback(
    (nextTableFilters: Record<string, any>) => {
      setTableFilters(nextTableFilters);
      onSearchChange?.(buildFilters(nextTableFilters, entityTypeFilter));
    },
    [buildFilters, entityTypeFilter, onSearchChange]
  );

  const handleEntityTypeChange = useCallback(
    (nextEntityType: string | null) => {
      setEntityTypeFilter(nextEntityType);
      onSearchChange?.(buildFilters(tableFilters, nextEntityType));
    },
    [buildFilters, onSearchChange, tableFilters]
  );

  const renderEntityTypeToolbar = () => (
    <Box sx={{ px: 1 }}>
      <Tabs
        value={entityTypeFilter ?? "all"}
        onChange={(_event, nextValue: string) =>
          handleEntityTypeChange(nextValue === "all" ? null : nextValue)
        }
        aria-label="document entity type tabs"
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 44,
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {ENTITY_TYPE_OPTIONS.map((option) => (
          <Tab
            key={option.tabValue}
            value={option.tabValue}
            label={option.label}
            sx={{ minHeight: 44, py: 0.75 }}
          />
        ))}
      </Tabs>
    </Box>
  );

  const renderGroupHeader = (
    _groupKey: string,
    groupItems: ExpiringDocumentDto[]
  ) => {
    const firstDocument = groupItems[0];
    if (!firstDocument) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "1rem" }}>
            {getEntityTypeIcon(firstDocument.entityType)}
          </Typography>
          <Chip
            label={firstDocument.entityType}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.75rem", fontWeight: 600 }}
          />
          <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
            {firstDocument.entityName}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 500 }}
        >
          {groupItems.length} document{groupItems.length !== 1 ? "s" : ""}
        </Typography>
      </Box>
    );
  };

  return (
    <DataTable
      items={documents}
      columns={DOCUMENT_COLUMNS}
      actions={DOCUMENT_ACTIONS}
      isLoading={isLoading}
      isLoadingRows={isLoadingRows}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      onLoadMore={onLoadMore}
      getRowId={(document) => document.id}
      toolbar={renderEntityTypeToolbar()}
      enableSearch
      searchableColumns={SEARCHABLE_COLUMNS}
      initialSearchFilters={initialTableFilters}
      onSearchChange={handleTableSearchChange}
      grouping={{
        getGroupKey: (document) => `${document.entityType}-${document.entityId}`,
        renderGroupHeader,
        collapsedItemLimit: COLLAPSED_DOC_LIMIT,
        groupLabel: "entities",
        itemLabel: "document",
      }}
    />
  );
};

export default ExpiringDocumentsTable;
